
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SwapRequest, SwapRequestStatus, updateSwapRequestStatus, editSwapRequest, deleteSwapRequest } from "@/services/myReagents";

export const useSwapRequestsManager = (onSwapRequestUpdated: () => void) => {
  const navigate = useNavigate();
  const [selectedRequest, setSelectedRequest] = useState<SwapRequest | null>(null);
  const [action, setAction] = useState<"approve" | "reject" | null>(null);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // New state for edit/delete dialogs
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const handleApprove = (request: SwapRequest) => {
    setSelectedRequest(request);
    setAction("approve");
    setMessage(""); 
  };
  
  const handleReject = (request: SwapRequest) => {
    setSelectedRequest(request);
    setAction("reject");
    setMessage(""); 
  };
  
  const handleEdit = (request: SwapRequest) => {
    setSelectedRequest(request);
    setIsEditDialogOpen(true);
  };
  
  const handleDelete = (request: SwapRequest) => {
    setSelectedRequest(request);
    setIsDeleteDialogOpen(true);
  };
  
  const handleClose = () => {
    setSelectedRequest(null);
    setAction(null);
  };
  
  const closeEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedRequest(null);
  };
  
  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedRequest(null);
  };
  
  const handleUpdateStatus = async () => {
    if (!selectedRequest || !action) return;
    
    setIsSubmitting(true);
    const status: SwapRequestStatus = action === "approve" ? "approved" : "rejected";
    const result = await updateSwapRequestStatus(
      selectedRequest.id,
      selectedRequest.reagents.user_id,
      status,
      message
    );
    setIsSubmitting(false);
    
    if (result.success) {
      setSelectedRequest(null);
      setAction(null);
      setMessage("");
      onSwapRequestUpdated();
      
      // Redirect to messages if approved and we have redirection info
      if (result.redirectToMessages) {
        const { userId, reagentId } = result.redirectToMessages;
        navigate(`/conversations/${userId}?reagentId=${reagentId}`);
      }
    }
  };
  
  const handleSaveEdit = async (data: { creditOffer: number; message: string }) => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    const success = await editSwapRequest(
      selectedRequest.id,
      data.creditOffer,
      data.message
    );
    setIsSubmitting(false);
    
    if (success) {
      closeEditDialog();
      onSwapRequestUpdated();
    }
  };
  
  const handleConfirmDelete = async () => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    const success = await deleteSwapRequest(selectedRequest.id);
    setIsSubmitting(false);
    
    if (success) {
      closeDeleteDialog();
      onSwapRequestUpdated();
    }
  };
  
  return {
    selectedRequest,
    action,
    message,
    isSubmitting,
    isEditDialogOpen,
    isDeleteDialogOpen,
    setMessage,
    handleApprove,
    handleReject,
    handleEdit,
    handleDelete,
    handleClose,
    closeEditDialog,
    closeDeleteDialog,
    handleUpdateStatus,
    handleSaveEdit,
    handleConfirmDelete
  };
};
